/**
 * Transform Dispatcher
 *
 * Routes to the appropriate layout strategy based on panel mode.
 * This is the main entry point for generating GridData from run data.
 */

import {getLayoutStrategy} from '../Layouts';
import {GridData, SlotRegistry, TransformInput} from '../types';

/**
 * Dispatches to the appropriate layout strategy based on panel mode.
 *
 * This is the main entry point for the layout system. It gets the
 * strategy from the registry and uses it to transform the data.
 *
 * @param input - Runs, step metadata, and configuration
 * @returns GridData structure ready for rendering
 */
export function dispatchTransform(input: TransformInput): GridData {
  const strategy = getLayoutStrategy(input.config);
  return strategy.transform(input);
}

/**
 * Builds slot registry for the given configuration.
 *
 * @param config - Panel configuration
 * @returns Slot registry with control placement
 */
export function buildSlotRegistry(
  config: TransformInput['config']
): SlotRegistry {
  const strategy = getLayoutStrategy(config);
  return strategy.buildSlotRegistry(config);
}

/**
 * Type guard to check if a transform input is valid.
 *
 * @param input - Potential transform input
 * @returns True if input has all required fields
 */
export function isValidTransformInput(input: any): input is TransformInput {
  if (!input) {
    return false;
  }

  return (
    Array.isArray(input.runs) &&
    input.stepMeta != null &&
    typeof input.currentStep === 'number' &&
    typeof input.currentIndex === 'number' &&
    input.config != null &&
    typeof input.allMasks === 'object' &&
    typeof input.allBoxes === 'object'
  );
}

/**
 * Safe version of dispatchTransform that returns empty GridData on invalid input.
 *
 * @param input - Potential transform input
 * @returns GridData or empty structure if input is invalid
 */
export function safeDispatchTransform(input: any): GridData {
  if (!isValidTransformInput(input)) {
    return {
      rows: [],
      metadata: {
        layoutType: 'gallery',
        totalCells: 0,
      },
    };
  }

  try {
    return dispatchTransform(input);
  } catch (error) {
    console.error('Transform dispatch failed:', error);
    return {
      rows: [],
      metadata: {
        layoutType: 'gallery',
        totalCells: 0,
      },
    };
  }
}
